import { z } from 'zod';
import { uuidSchema } from '../common';
import type { Connector } from '../../catalog/types';

export const connectionIdParam = z.object({ id: uuidSchema });
export const connectorIdParam = z.object({ id: uuidSchema });

export const listConnectionsQuery = z.object({ hub_id: uuidSchema });

export const deleteConnectionQuery = z.object({ hub_id: uuidSchema });

export const addConnectionBody = z.object({
  hub_id: uuidSchema,
  connector_id: uuidSchema,
  authentication_type: z.string().optional(),
  inbound_mode: z.enum(['wayai', 'resend']).optional(),
  inbound_address: z.string().email().optional(),
  // Opt out of copying this connection's credentials into production on publish/sync.
  // Defaults to true at the DB layer.
  sync_credentials_to_production: z.boolean().optional(),
}).passthrough().superRefine((data, ctx) => {
  if (data.inbound_mode === 'resend' && !data.inbound_address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Receiving email address is required when using Resend inbound mode',
      path: ['inbound_address'],
    });
  }
});

export const editConnectionBody = z.object({
  hub_id: uuidSchema,
  sync_credentials_to_production: z.boolean().optional(),
}).passthrough();

/**
 * PATCH /connections/:id/production-credential — sets a credential directly on a
 * PRODUCTION connection (the sanctioned carve-out from production read-only, used when
 * `sync_credentials_to_production` is off). `.strict()` so only credential-source fields
 * can be written — never arbitrary connection config on a production hub. Exactly one
 * source: either an org-credential link (`organization_credential_id`, optionally
 * `organization_credential_token_id`) OR direct raw secret field(s).
 */
const PRODUCTION_DIRECT_FIELDS = ['username', 'api_key', 'access_token', 'password', 'refresh_token', 'webhook_secret_token', 'webhook_signing_secret'] as const;
export const setProductionCredentialBody = z.object({
  hub_id: uuidSchema,
  organization_credential_id: z.string().nullable().optional(),
  organization_credential_token_id: z.string().nullable().optional(),
  username: z.string().optional(),
  api_key: z.string().optional(),
  access_token: z.string().optional(),
  password: z.string().optional(),
  refresh_token: z.string().optional(),
  webhook_secret_token: z.string().optional(),
  webhook_signing_secret: z.string().optional(),
}).strict().superRefine((data, ctx) => {
  const hasOrgLink = !!data.organization_credential_id || !!data.organization_credential_token_id;
  const hasDirect = PRODUCTION_DIRECT_FIELDS.some((f) => data[f] != null);
  if (hasOrgLink && hasDirect) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Provide either an organization credential link or direct credential fields, not both' });
  }
  if (!hasOrgLink && !hasDirect) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A credential is required: link an organization credential or provide a credential field' });
  }
});

export const registerWhatsappQuery = z.object({ hub_id: uuidSchema });

export const registerWhatsappBody = z.object({
  pin: z.string().optional(),
});

export type AddConnectionInput = z.infer<typeof addConnectionBody>;
export type EditConnectionInput = z.infer<typeof editConnectionBody>;
export type SetProductionCredentialInput = z.infer<typeof setProductionCredentialBody>;
export type RegisterWhatsappInput = z.infer<typeof registerWhatsappBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** Single connection item as returned by the `get_connections` RPC. */
export interface ConnectionItemResponse {
  connection_id: string | null;
  hub_id: string;
  account_sid: string | null;
  connection_display_name: string | null;
  username: string | null;
  state: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  organization_credential_id: string | null;
  organization_credential_token_id: string | null;
  connector_id: string;
  connector_name: string;
  connector_type: string;
  service_name: string;
  authentication_type: string;
  authentication_types: string[];
  unique_per_hub: boolean;
  account_sid_placeholder: string | null;
  init_endpoint: string | null;
  connector_credential_schema: Record<string, unknown> | null;
  connector_settings_schema: Record<string, unknown> | null;
  agent_settings_schema: Record<string, unknown> | null;
  available_action: 'Add' | 'Edit' | 'Remove' | 'Enable' | 'Disable';
  enabled: boolean;
  sync_credentials_to_production: boolean;
  base_url: string | null;
  connection_headers: Record<string, unknown> | null;
}

/** Full connection row with nested connector, returned by GET /connections/:id. */
export interface ConnectionWithConnectorResponse {
  connection_id: string;
  created_at: string;
  service_name: string | null;
  api_key: string | null;
  updated_at: string | null;
  hub_id: string;
  access_token: string | null;
  access_token_expires_at: string | null;
  access_token_secret_id: string | null;
  account_sid: string | null;
  api_key_secret_id: string | null;
  authentication_type: string;
  enabled: boolean;
  connector_id: string;
  connector_name: string;
  password: string | null;
  password_secret_id: string | null;
  connection_display_name: string | null;
  connector_type: string;
  refresh_token: string | null;
  refresh_token_expires_at: string | null;
  refresh_token_secret_id: string | null;
  sync_credentials_to_production: boolean;
  state: Record<string, unknown> | null;
  username: string | null;
  base_url: string | null;
  connection_headers: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  token_refresh_config: Record<string, unknown> | null;
  next_access_token_refresh_at: string | null;
  next_refresh_token_renewal_at: string | null;
  last_token_refresh_at: string | null;
  last_token_refresh_error: string | null;
  organization_credential_id: string | null;
  organization_credential_token_id: string | null;
  available_action: 'Add' | 'Edit' | 'Remove' | 'Enable' | 'Disable';
  connector_credential_schema: Record<string, unknown> | null;
  connector_settings_schema: Record<string, unknown> | null;
  agent_settings_schema: Record<string, unknown> | null;
  connector: ConnectorResponse;
}

/** Connector row returned by GET /connectors/:id and as nested data. */
export type ConnectorResponse = Connector;

/** GET /connections?hub_id=... */
export interface ListConnectionsResponse {
  connections: ConnectionItemResponse[];
}

/** GET /connections/:id */
export interface GetConnectionResponse {
  data: ConnectionWithConnectorResponse;
}

/** POST /connections (add connection — may return OAuth redirect) */
export interface AddConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
  oauth_url?: string;
  state?: string;
}

/** PATCH /connections/:id (edit connection) */
export interface EditConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
}

/** PATCH /connections/:id/production-credential (set production connection credential) */
export interface SetProductionCredentialResponse {
  connections: ConnectionItemResponse[];
}

/** POST /connections/:id/enable */
export interface EnableConnectionResponse {
  connections: ConnectionItemResponse[];
  warnings?: string[];
}

/** POST /connections/:id/disable */
export interface DisableConnectionResponse {
  connections: ConnectionItemResponse[];
}

/** DELETE /connections/:id */
export interface DeleteConnectionResponse {
  connections: ConnectionItemResponse[];
}

/** GET /connections/:id/channel-info */
export interface ChannelInfoResponse {
  channel: Record<string, unknown> | null;
  connection: Record<string, unknown>;
}

/** POST /connections/:id/sync-mcp */
export interface SyncMcpConnectionResponse {
  success: boolean;
  tools_count?: number;
  resources_count?: number;
  message?: string;
  warnings?: string[];
}

/** POST /connections/:id/register-whatsapp */
export interface RegisterWhatsappResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/** GET /connectors/:id */
export interface GetConnectorResponse {
  data: ConnectorResponse;
}

// =============================================================================
// ASYNC PROVISIONING RETRY
// =============================================================================

/** POST /connections/:id/retry-provisioning — query */
export const retryProvisioningQuery = z.object({ hub_id: uuidSchema });

/** POST /connections/:id/retry-provisioning — response */
export interface RetryProvisioningResponse {
  success: true;
  connection_id: string;
}

// =============================================================================
// RESEND DOMAIN VERIFICATION
// =============================================================================

export const resendDomainStatusQuery = z.object({ hub_id: uuidSchema });

export interface ResendDnsRecord {
  record: string;
  name: string;
  type: string;
  ttl: string;
  status: string;
  value: string;
  priority?: number;
}

/** GET /connections/:id/domain-status */
export interface ResendDomainStatusResponse {
  verified: boolean;
  domain_id: string | null;
  domain_name: string | null;
  status: string;
  records: ResendDnsRecord[];
  inbound_address: string | null;
  inbound_mode: 'wayai' | 'resend' | null;
}

/** POST /connections/:id/verify-domain */
export interface ResendVerifyDomainResponse {
  domain_id: string;
  status: string;
}
